import { ref, watch, nextTick } from "vue";
import { useFetch } from "./fetch";
import { FileAttachment, Thumbnail } from "@/bapi-client/types/common/attachment";
import { AttachmentResponse } from "@/types/notes";

export function useAttachments() {
  // Provide a unique identifier to attach attachments to
  // could be a note ID, quote ID, etc.
  const attachments = ref<Record<string, AttachmentResponse[]>>({});
  const request = useFetch();

  // Provide the SAME unique ID here, so that thumbnails can
  // be tied back to arbitrary entities like a quote, comment,
  // note, project, user, etc.
  const thumbnails = ref<Record<string, Thumbnail[]>>({});
  const files = ref<Record<string, FileAttachment[]>>({});

  // Track loading states to prevent duplicate requests
  const loadingStates = ref<Record<string, Set<string>>>({});

  /**
   * Safely revoke object URLs to prevent memory leaks
   */
  function revokeThumbnailUrls(id: string) {
    if (thumbnails.value[id]) {
      thumbnails.value[id].forEach((thumb) => {
        if (thumb.src && thumb.src.startsWith("blob:")) {
          URL.revokeObjectURL(thumb.src);
        }
      });
    }
  }

  /**
   * Clear all thumbnails and files for a specific entity ID
   */
  function clearAttachments(id: string) {
    revokeThumbnailUrls(id);
    if (thumbnails.value[id]) {
      thumbnails.value[id] = [];
    }
    if (files.value[id]) {
      files.value[id] = [];
    }
    if (attachments.value[id]) {
      attachments.value[id] = [];
    }
  }

  /**
   * Load thumbnails for a specific entity ID
   */
  async function loadThumbnails(id: string) {
    if (!thumbnails.value[id]?.length) return;

    const thumbnailsToLoad = thumbnails.value[id].filter((thumb) => !thumb.src && !thumb.loading);

    for (const thumbnail of thumbnailsToLoad) {
      if (loadingStates.value[id]?.has(thumbnail.id)) continue;

      try {
        // Mark as loading
        if (!loadingStates.value[id]) loadingStates.value[id] = new Set();
        loadingStates.value[id].add(thumbnail.id);

        thumbnail.loading = true;

        const response = await request.get(thumbnail.url);
        if (!response.ok) {
          console.warn(`Failed to load thumbnail for ${thumbnail.id}: ${response.status}`);
          continue;
        }

        const data = await response.blob();
        if (data) {
          thumbnail.src = URL.createObjectURL(data);
        }
      } catch (error) {
        console.error(`Error loading thumbnail for ${thumbnail.id}:`, error);
      } finally {
        thumbnail.loading = false;
        loadingStates.value[id]?.delete(thumbnail.id);
      }
    }
  }

  /**
   * Preview a file and return its blob URL
   */
  async function previewFile(id: string, fileId: string): Promise<string | undefined> {
    const attachment = attachments.value[id]?.find((att) => att.id === fileId);
    if (!attachment) return undefined;

    try {
      const response = await request.get(attachment.normalized_url);
      if (!response.ok) {
        console.warn(`Failed to preview file ${fileId}: ${response.status}`);
        return undefined;
      }

      const data = await response.blob();
      if (!data) return undefined;

      return URL.createObjectURL(data);
    } catch (error) {
      console.error(`Error previewing file ${fileId}:`, error);
      return undefined;
    }
  }

  /**
   * Download a file
   */
  async function downloadFile(id: string, fileId: string): Promise<undefined | true> {
    const attachment = attachments.value[id]?.find((att) => att.id === fileId);
    if (!attachment) return undefined;

    try {
      const response = await request.get(attachment.download_url);
      if (!response.ok) {
        console.warn(`Failed to download file ${fileId}: ${response.status}`);
        return undefined;
      }

      const data = await response.blob();
      if (!data) return undefined;

      const link = window.document.createElement("a");
      const url = URL.createObjectURL(data);
      link.href = url;
      link.download = attachment.filename;
      window.document.body.append(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error(`Error downloading file ${fileId}:`, error);
      return undefined;
    }
  }

  /**
   * Updates attachments for a specific entity ID, only if they have actually changed.
   * This prevents unnecessary updates and preserves existing thumbnail/file data.
   */
  function updateAttachments(id: string, newAttachments: AttachmentResponse[]): boolean {
    const currentAttachments = attachments.value[id] || [];

    // Check if attachments have actually changed
    const attachmentsChanged = JSON.stringify(newAttachments) !== JSON.stringify(currentAttachments);

    if (attachmentsChanged) {
      // Clean up old thumbnails before updating
      revokeThumbnailUrls(id);

      attachments.value[id] = newAttachments;
      return true;
    } else {
      return false;
    }
  }

  /**
   * Process attachments and create thumbnails/files after attachments are updated
   */
  async function processAttachments(id: string) {
    const entityAttachments = attachments.value[id] || [];

    // Initialize arrays if they don't exist
    if (!thumbnails.value[id]) thumbnails.value[id] = [];
    if (!files.value[id]) files.value[id] = [];

    // Get current attachment IDs for comparison
    const currentAttachmentIds = new Set(entityAttachments.map((att) => att.id));

    // Remove thumbnails and files for attachments that no longer exist
    thumbnails.value[id] = thumbnails.value[id].filter((thumb) => currentAttachmentIds.has(thumb.id));
    files.value[id] = files.value[id].filter((file) => currentAttachmentIds.has(file.id));

    // Process new attachments
    for (const att of entityAttachments) {
      if (att.mime_type.startsWith("image/")) {
        // Handle image attachments (thumbnails)
        const existingThumb = thumbnails.value[id].find((thumb) => thumb.id === att.id);
        if (!existingThumb) {
          thumbnails.value[id].push({
            id: att.id,
            url: att.thumbnail_url,
            loading: false,
            src: "",
          });
        }
      } else {
        // Handle non-image attachments (files)
        const existingFile = files.value[id].find((file) => file.id === att.id);
        if (!existingFile) {
          files.value[id].push({
            id: att.id,
            fileName: att.filename,
            fileSize: att.filesize,
            mimeType: att.mime_type,
            extension: att.extension,
          });
        }
      }
    }

    // Load thumbnails after processing (only if there are attachments)
    if (entityAttachments.length > 0) {
      await nextTick();
      await loadThumbnails(id);
    }
  }

  // Watch for attachment changes and process them
  watch(
    () => attachments.value,
    async () => {
      for (const id in attachments.value) {
        if (attachments.value[id] !== undefined) {
          await processAttachments(id);
        }
      }
    },
    { deep: true },
  );

  return {
    attachments,
    thumbnails,
    files,
    loadThumbnails,
    downloadFile,
    previewFile,
    updateAttachments,
    clearAttachments,
  };
}
